import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@elaniintc/goes-design-system";
import type { DCOperation } from "../types";
import { DCScopeConditionSelector } from "./scope-condition-selector";
import { TypeValueInput } from "./ValueInput";
import type { Variable } from "../../types";
import { useDataCenterNode } from "../context";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";

export interface ScopeState {
  scope: ScopeItem;
  conditionScope: DCOperation;
  operationType: "input" | "variable";
  comparativeValue: string | Variable;
  initialRange: { value: string | Variable; type: "input" | "variable" };
  endRange: { value: string | Variable; type: "input" | "variable" };
  dateRangeError: string;
}

export type ScopeItemType = "created_at" | "updated_at";

export interface ScopeItem {
  id: ScopeItemType;
  name: string;
  type: "dateScope";
  data_type: "date";
}

// eslint-disable-next-line react-refresh/only-export-components
export const scopeItems: ScopeItem[] = [
  {
    id: "created_at",
    name: "Fecha de creación",
    type: "dateScope",
    data_type: "date",
  },
  {
    id: "updated_at",
    name: "Fecha de última edición",
    type: "dateScope",
    data_type: "date",
  },
];

export interface ScopeSelectorProps {
  selected: ScopeItem | null;
  onSelectScope: (scope: ScopeItem) => void;
  placeholder?: string;
  group?:
    | "middle"
    | "up-left"
    | "up-right"
    | "left"
    | "right"
    | "down"
    | "middle-left"
    | "middle-right"
    | "middle-up"
    | "down-left"
    | "down-right"
    | "up"
    | "single";
}

export const DCScopeSelector = ({
  selected,
  onSelectScope,
  placeholder = "Seleccione un alcance",
  group = "left",
}: ScopeSelectorProps) => {
  return (
    <Select
      onValueChange={(val) => {
        const variable = scopeItems.find((v) => v.id === val);
        if (variable) {
          onSelectScope(variable);
        }
      }}
      value={selected?.id || ""}
    >
      <SelectTrigger className="min-w-0 w-full flex" group={group}>
        <div className="w-full flex gap-3 items-center">
          <p className="truncate text-body">{selected?.name ?? placeholder}</p>
        </div>
      </SelectTrigger>
      <SelectContent
        align="start"
        side="bottom"
        className="max-h-[440px] overflow-y-auto border border-neutral py-2"
      >
        {scopeItems.map((variable) => (
          <SelectItem
            key={`${variable.id}-${variable.name}`}
            value={variable.id}
          >
            <div className="w-full flex items-center gap-3">
              <div className="flex gap-2 items-center">
                <span className="text-body">{variable.name}</span>
              </div>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const ScopeField = () => {
  const { scopeState, setScopeState } = useDataCenterNode();
  const { variables } = useGetVariables();
  const {
    scope,
    conditionScope,
    operationType,
    comparativeValue,
    initialRange,
    endRange,
  } = scopeState;

  /**
   * Validate if the range is valid
   * If the values are not strings, return true
   * @param startDate
   * @param endDate
   * @returns
   */
  const validateDateRange = (
    startDate: string | Variable,
    endDate: string | Variable,
  ) => {
    if (
      typeof startDate === "string" &&
      typeof endDate === "string" &&
      startDate &&
      endDate
    ) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        //compare dates without time
        const startDateOnly = new Date(
          start.getFullYear(),
          start.getMonth(),
          start.getDate(),
        );
        const endDateOnly = new Date(
          end.getFullYear(),
          end.getMonth(),
          end.getDate(),
        );

        return endDateOnly > startDateOnly;
      }
    }
    return true;
  };

  const handleScopeChange = (newScope: ScopeItem) => {
    setScopeState({
      ...scopeState,
      scope: newScope,
      conditionScope: { name: "ANY", label: "Cualquiera" },
      operationType: "input",
      comparativeValue: "",
      initialRange: { value: "", type: "input" },
      endRange: { value: "", type: "input" },
      dateRangeError: "",
    });
  };

  const handleScopeConditionChange = (newCondition: DCOperation) => {
    setScopeState({
      ...scopeState,
      conditionScope: newCondition,
      comparativeValue: "",
      initialRange: { value: "", type: "input" },
      endRange: { value: "", type: "input" },
      dateRangeError: "",
    });
  };

  return (
    <GroupTextField
      label="Alcance"
      required
      className="w-full pt-3"
      helperText="Filtra los registros según su metadata."
      error={scopeState.dateRangeError}
    >
      <GroupTextFieldRow>
        {/* Scope */}
        <DCScopeSelector
          selected={scope}
          onSelectScope={handleScopeChange}
          group={
            conditionScope?.needsComparativeValue ||
            conditionScope?.needsComparativeRange
              ? "up-left"
              : "left"
          }
        />
        {/* Condition */}
        <DCScopeConditionSelector
          scopeType={scope.type ?? "dateScope"}
          selected={conditionScope}
          onSelectCondition={handleScopeConditionChange}
          group={
            conditionScope?.needsComparativeValue ||
            conditionScope?.needsComparativeRange
              ? "up-right"
              : "right"
          }
        />
      </GroupTextFieldRow>
      {conditionScope.needsComparativeValue && (
        <GroupTextFieldRow>
          <Button
            variant="quiet"
            type="button"
            className="font-mono"
            onClick={() => {
              setScopeState({
                ...scopeState,
                operationType:
                  operationType === "variable" ? "input" : "variable",
                comparativeValue: "",
                initialRange: { value: "", type: "input" },
                endRange: { value: "", type: "input" },
              });
            }}
          >
            {operationType === "variable" ? "val" : "inp"}
          </Button>
          <TypeValueInput
            inputType={operationType}
            valueType={"date"}
            value={comparativeValue}
            onValueChange={(value) =>
              setScopeState({ ...scopeState, comparativeValue: value })
            }
            inputVariables={variables}
            filterByType="date"
          />
        </GroupTextFieldRow>
      )}

      {conditionScope.needsComparativeRange && (
        <GroupTextFieldRow>
          {/* Initial Range Value */}
          <Button
            variant="quiet"
            type="button"
            className="font-mono"
            onClick={() => {
              setScopeState({
                ...scopeState,
                initialRange: {
                  value: "",
                  type:
                    initialRange?.type === "variable" ? "input" : "variable",
                },
              });
            }}
          >
            {initialRange?.type === "variable" ? "val" : "inp"}
          </Button>
          <TypeValueInput
            inputType={initialRange?.type}
            valueType={scope.type === "dateScope" ? "date" : "variable"}
            value={initialRange?.value}
            onValueChange={(value) => {
              const newInitialRange = { ...initialRange, value };
              if (
                endRange?.value &&
                !validateDateRange(value, endRange.value)
              ) {
                setScopeState({
                  ...scopeState,
                  initialRange: newInitialRange,
                  endRange: { ...endRange, value: "" },
                  dateRangeError: "",
                });
              } else {
                setScopeState({
                  ...scopeState,
                  initialRange: newInitialRange,
                  dateRangeError: "",
                });
              }
            }}
            placeholder="Fecha inicial"
            showInputIcon={false}
            inputVariables={variables}
            filterByType="date"
          />
          {/* End Range Value */}
          <Button
            variant="quiet"
            type="button"
            className="font-mono border-t-0"
            onClick={() => {
              setScopeState({
                ...scopeState,
                endRange: {
                  value: "",
                  type: endRange?.type === "variable" ? "input" : "variable",
                },
              });
            }}
          >
            {endRange?.type === "variable" ? "val" : "inp"}
          </Button>
          <TypeValueInput
            inputType={endRange?.type}
            valueType={scope.type === "dateScope" ? "date" : "variable"}
            value={endRange?.value}
            onValueChange={(value) => {
              if (
                initialRange?.value &&
                !validateDateRange(initialRange.value, value)
              ) {
                setScopeState({
                  ...scopeState,
                  dateRangeError:
                    "La fecha final debe ser posterior a la fecha inicial",
                });
                return;
              }
              setScopeState({
                ...scopeState,
                endRange: { ...endRange, value },
                dateRangeError: "",
              });
            }}
            placeholder="Fecha final"
            showInputIcon={false}
            inputVariables={variables}
          />
        </GroupTextFieldRow>
      )}
    </GroupTextField>
  );
};
